// frontend/src/components/VetClinicStartedDrawer.js

import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { CheckCircleIcon, CheckIcon } from "@heroicons/react/24/outline";
import PetDetailsHeader from "./petDetailsHaeder";
import VetStartedSOAPTabs from "./VetStartedSOAPTabs";
import ServiceDetailsCard from "./ServiceDetailsCard";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const VetClinicStartedDrawer = ({
  open,
  booking,
  onClose,
  appointmentRow,
  onComplete,
}) => {
  const customer = booking?.customer || {};
  const service = booking?.service || {};
  const pricing = booking?.pricing || {};
  const raw = useMemo(() => appointmentRow || {}, [appointmentRow]);

  const clientName =
    customer.name ||
    `${raw.client_first_name || ""} ${raw.client_last_name || ""}`.trim() ||
    "—";
  const clientPhone = customer.phone || raw.phone || "";
  const clientEmail = customer.email || raw.email || "";
  const clientId = booking?.pet?.vendor_client_id || raw.vendor_client_id || "";
  const staffName = service.staffName || raw.employee_name || "";
  const orderId = booking?.order_id || raw.order_id || "";
  // List APIs expose ref_id; single getVendorAppointment returns _id.
  const refId = String(booking?.booking_id || raw.ref_id || raw._id || "");
  const vendorPetId =
    booking?.pet?.vendor_pet_id || raw.vendor_pet_id || raw.pet_id || "";
  const totalAmount = pricing.total || raw.total_price || 0;

  const appointmentId = String(
    raw?.ref_id || raw?._id || booking?.booking_id || "",
  );

  const soapTabsRef = useRef();
  const petHeaderRef = useRef();

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [soapNote, setSoapNote] = useState(null);
  const [loadingNote, setLoadingNote] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [headerRefreshTrigger, setHeaderRefreshTrigger] = useState(0);

  const [isServiceCompleted, setIsServiceCompleted] = useState(false);
  const [liveBillingSummary, setLiveBillingSummary] = useState(null);
  const [isSingleServiceOrder, setIsSingleServiceOrder] = useState(false);

  // ─── Service Details Card state (read-only, display only) ─────────────
  const [serviceDetails, setServiceDetails] = useState({
    name: service.service_name || raw.service_name || "",
    duration: service.duration || raw.duration || "",
    date: service.start_date || raw.start_date || "",
    time: service.start_time || raw.start_time || "",
    price:
      service.total_price || raw.total_price || raw.unit_price_with_tax || 0,
  });
  const [serviceOrderItem, setServiceOrderItem] = useState(null);

  const serviceCategory =
    service.service_category_name ||
    raw.service_category_name ||
    raw.service_type_name ||
    "";

  const handleDirectPaymentSuccess = useCallback(() => {
    setLiveBillingSummary(null);
  }, []);

  const refreshHeader = useCallback(() => {
    setHeaderRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!refId) {
      setLoadingNote(false);
      setLoadError(true);
      return;
    }
    let cancelled = false;
    setLoadingNote(true);
    setLoadError(false);

    httpService
      .get(`/vendorpet/getvendorsoap/${refId}`)
      .then((res) => {
        if (!cancelled) {
          setSoapNote(res?.data || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(true);
          toast.error("Failed to load consultation note");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingNote(false);
      });

    const loadInitialData = async () => {
      try {
        const isSingleServiceOrderRes = await httpService.post(
          `/vendororder/isSingleServiceOrder`,
          { orderId },
        );
        if (isSingleServiceOrderRes?.status)
          setIsSingleServiceOrder(
            isSingleServiceOrderRes?.data?.isSingleServiceOrder,
          );
        else setIsSingleServiceOrder(false);
      } catch (error) {
        console.error("Error fetching order:", error);
      } finally {
      }
    };
    loadInitialData();
    return () => {
      cancelled = true;
    };
  }, [open, refId]);

  useEffect(() => {
    if (raw?.status) {
      const status = String(raw.status).toLowerCase().trim();
      if (
        ["completed", "complete", "closed", "checkout", "checked_out"].includes(
          status,
        )
      ) {
        setIsServiceCompleted(true);
      } else {
        setIsServiceCompleted(false);
      }
    }
  }, [raw]);

  // Fresh order status on open — parent row can be stale after mark-complete / close.
  useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;

    const syncStatusFromOrder = async () => {
      try {
        const res = await httpService.get(
          `/vendororder/getvendororder/${orderId}`,
        );
        if (cancelled || res?.code !== 200) return;
        const items = res.data?.order_items || [];
        const matched =
          items.find((i) => String(i.ref_id) === String(refId)) ||
          items.find((i) => String(i.vendor_pet_id) === String(vendorPetId)) ||
          items[0];
        const status = String(matched?.status || res.data?.status || "")
          .toLowerCase()
          .trim();
        if (
          ["completed", "complete", "closed", "checkout", "checked_out"].includes(
            status,
          )
        ) {
          setIsServiceCompleted(true);
        }
      } catch (err) {
        console.error("Failed to sync appointment status from order:", err);
      }
    };

    syncStatusFromOrder();
    return () => {
      cancelled = true;
    };
  }, [open, orderId, refId, vendorPetId]);

  // ─── Fetch matched order item to hydrate the ServiceDetailsCard ───────
  useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await httpService.get(
          `/vendororder/getvendororder/${orderId}`,
        );
        if (cancelled || res?.code !== 200 || !res?.data) return;

        const items = Array.isArray(res.data.order_items)
          ? res.data.order_items
          : [];

        const matchedItem =
          items.find(
            (item) =>
              String(item?.ref_id) === String(refId) &&
              String(item?.vendor_pet_id) === String(vendorPetId),
          ) ||
          items.find(
            (item) =>
              String(item?.vendor_pet_id) === String(vendorPetId) &&
              ["vet", "vet_clinic", "veterinary"].includes(
                String(item?.item_type || "").toLowerCase(),
              ),
          ) ||
          items.find((item) => String(item?.ref_id) === String(refId));

        if (!matchedItem) return;

        setServiceOrderItem(matchedItem);
        setServiceDetails((prev) => ({
          ...prev,
          name: matchedItem.service_name || prev.name,
          duration:
            matchedItem.duration !== undefined &&
            matchedItem.duration !== null
              ? matchedItem.duration
              : prev.duration,
          date: matchedItem.start_date || prev.date,
          time: matchedItem.start_time || prev.time,
          price:
            matchedItem.total_price ||
            matchedItem.final_total ||
            matchedItem.price ||
            prev.price,
        }));
      } catch (err) {
        console.error("Error fetching order for ServiceDetailsCard:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, orderId, refId, vendorPetId]);

  const handleClose = async () => {
    if (hasChanges) {
      const success = await soapTabsRef.current?.save?.(false);
      if (!success) {
        const discard = window.confirm(
          "We couldn't save your changes. Close anyway and lose them?",
        );
        if (!discard) return;
      }
    }
    onClose?.();
  };

  const handleMarkComplete = async () => {
    if (hasChanges) {
      setIsSaving(true);
      try {
        const saved = await soapTabsRef.current?.save?.(false);
        if (!saved) {
          toast.error("Please save your changes before completing");
          setIsSaving(false);
          return;
        }
      } catch (err) {
        toast.error("Failed to save changes");
        setIsSaving(false);
        return;
      } finally {
        setIsSaving(false);
      }
    }

    const result = await Swal.fire({
      title: "Complete Consultation?",
      text: "This will mark the service as completed and finalize the SOAP note.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Complete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setIsCompleting(true);

    try {
      const soapSuccess = await soapTabsRef.current?.save?.(true);

      if (!soapSuccess) {
        toast.error("Failed to save SOAP note");
        setIsCompleting(false);
        return;
      }

      if (appointmentId) {
        const response = await httpService.put(
          `/vendorappointment/updateVendorAppointmentStatus/${appointmentId}`,
          { status: "completed" },
        );

        if (response?.code === 200 || response?.status) {
          setIsServiceCompleted(true);
          toast.success("Consultation marked as complete!");

          if (isSingleServiceOrder) {
            onClose?.();
            onComplete?.(orderId);
          } else onClose?.();

          setTimeout(() => {
            onClose?.();
          }, 1500);
        } else {
          throw new Error(
            response?.message || "Failed to update service status",
          );
        }
      } else {
        setIsServiceCompleted(true);
        toast.success("Consultation note marked as complete!");

        setTimeout(() => {
          onClose?.();
        }, 1500);
      }
    } catch (err) {
      console.error("Error marking complete:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to mark consultation as complete",
      );
    } finally {
      setIsCompleting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div
        className="fixed right-0 top-0 h-full z-50 flex flex-col bg-white shadow-2xl w-full md:w-[90vw] lg:w-[90vw] xl:w-[85vw] 2xl:w-[80vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <PetDetailsHeader
          ref={petHeaderRef}
          orderId={orderId}
          vendorPetId={vendorPetId}
          activeServiceRefId={refId}
          clientName={clientName}
          clientPhone={clientPhone}
          clientEmail={clientEmail}
          staffName={staffName}
          clientId={clientId}
          showOnlySelectedPet={true}
          onClose={handleClose}
          refreshTrigger={headerRefreshTrigger}
          liveBillingSummary={liveBillingSummary}
          onDirectPaymentSuccess={handleDirectPaymentSuccess}
        />

        {/* ─── Read-only Service Details Card ───────────────────────────── */}
        <div className="px-2 pt-2 shrink-0">
          <ServiceDetailsCard
            serviceCategory={serviceCategory}
            serviceDetails={serviceDetails}
            staffName={staffName}
            boardingOrderItem={serviceOrderItem}
            addons={[]}
            products={[]}
            otherCharges={[]}
            rawData={raw}
            orderId={orderId}
            clientId={clientId}
            clientName={clientName}
            clientPhone={clientPhone}
            vendorPetId={vendorPetId}
            readOnly={true}
          />
        </div>

        <div className="flex flex-1 overflow-hidden min-h-0 gap-4">
          <div className="flex-1 flex flex-col overflow-hidden pr-2">
            {loadingNote ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex items-center gap-2 text-slate-400 text-[12px]">
                  <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  Loading consultation note…
                </div>
              </div>
            ) : loadError ? (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-[12px] text-red-500">
                  Couldn't load the consultation note. Please close and reopen.
                </p>
              </div>
            ) : (
              <VetStartedSOAPTabs
                ref={soapTabsRef}
                refId={refId}
                vendorPetId={vendorPetId}
                orderId={orderId}
                bookingStatus={booking?.status || "upcoming"}
                readOnly={isServiceCompleted}
                onDirtyChange={setHasChanges}
                consultationFee={totalAmount}
                initialData={soapNote}
                refreshBilling={refreshKey}
                onBillingChange={refreshHeader}
                onBillingSummaryChange={setLiveBillingSummary}
              />
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {isServiceCompleted ? (
              <span className="flex items-center gap-1.5 text-[11px] text-emerald-600 font-semibold">
                <CheckCircleIcon className="w-3.5 h-3.5" />
                Consultation Complete
              </span>
            ) : hasChanges ? (
              <span className="flex items-center gap-1.5 text-[11px] text-amber-600 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                {isSaving ? "Saving..." : "Unsaved changes"}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-[11px] text-emerald-600 font-semibold">
                <CheckCircleIcon className="w-3.5 h-3.5" />
                All saved
              </span>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-slate-200 rounded-xl text-[12px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Close
            </button>

            {!isServiceCompleted ? (
              <button
                onClick={handleMarkComplete}
                disabled={isCompleting || isSaving}
                className="px-5 py-2 bg-emerald-600 text-white rounded-xl text-[12px] font-bold hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
              >
                {isCompleting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    Mark Complete
                  </>
                )}
              </button>
            ) : (
              <button
                disabled
                className="px-5 py-2 bg-slate-300 text-slate-500 rounded-xl text-[12px] font-bold cursor-not-allowed transition-colors shadow-sm flex items-center gap-2"
              >
                <CheckCircleIcon className="w-4 h-4" />
                Completed
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VetClinicStartedDrawer;